import React, { useState } from 'react';
import { Form, Input, Select, Button, Card, Typography, Row, Col, message, Steps, InputNumber } from 'antd';
import { UserOutlined, EnvironmentOutlined, TeamOutlined, TrophyOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AddressInput from '../../components/AddressInput';

const { Title, Text } = Typography;
const { Option } = Select;

const PlayerRegister = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // A conta eh unificada (mesmo cadastro serve pra jogar e pra organizar time).
  // O que muda eh o fluxo pos-cadastro: se veio pra criar time, redireciona
  // pra /time/setup e mostra copy contextual durante o form.
  const next = searchParams.get('next') || '';
  const isOrganizerFlow = next.startsWith('/time');

  const skillLevels = [
    { value: 'iniciante', label: 'Iniciante' },
    { value: 'recreativo', label: 'Recreativo' },
    { value: 'intermediario', label: 'Intermediário' },
    { value: 'avancado', label: 'Avançado' },
    { value: 'semi_pro', label: 'Semi-pro' }
  ];

  const positions = [
    { value: 'goleiro', label: 'Goleiro' },
    { value: 'zagueiro', label: 'Zagueiro' },
    { value: 'lateral', label: 'Lateral' },
    { value: 'volante', label: 'Volante' },
    { value: 'meia', label: 'Meia' },
    { value: 'atacante', label: 'Atacante' },
    { value: 'qualquer', label: 'Qualquer' }
  ];

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/community/players/register', values);
      
      if (response.data.success) {
        message.success('Cadastro realizado com sucesso!');
        setCurrentStep(1);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Erro ao realizar cadastro');
    }
    setLoading(false);
  };

  const steps = isOrganizerFlow
    ? [
        { title: 'Sua conta', icon: <UserOutlined /> },
        { title: 'Cadastrar time', icon: <TeamOutlined /> },
        { title: 'Concluído', icon: <TrophyOutlined /> },
      ]
    : [
        { title: 'Cadastro', icon: <UserOutlined /> },
        { title: 'Concluído', icon: <EnvironmentOutlined /> },
      ];

  if (currentStep === 1) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '20px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', paddingTop: '10vh' }}>
          <Card style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: '#52c41a' }}>
              {isOrganizerFlow ? 'Conta criada!' : 'Cadastro Realizado!'}
            </Title>
            <Text>
              {isOrganizerFlow
                ? 'Bora cadastrar seu time agora. Faça login e siga o passo a passo.'
                : 'Agora você pode fazer login e começar a buscar times!'}
            </Text>
            <div style={{ marginTop: 24 }}>
              <Button type="primary"
                onClick={() => {
                  const loginPath = isOrganizerFlow
                    ? `/player/login?next=${encodeURIComponent(next)}`
                    : '/player/login';
                  navigate(loginPath);
                }}>
                {isOrganizerFlow ? 'Entrar e cadastrar time →' : 'Fazer Login'}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f0f2f5', 
      padding: window.innerWidth < 768 ? '8px' : '20px' 
    }}>
      <div style={{ 
        maxWidth: window.innerWidth < 768 ? '100%' : 800, 
        margin: '0 auto', 
        paddingTop: window.innerWidth < 768 ? '2vh' : '5vh' 
      }}>
        <Card size={window.innerWidth < 768 ? 'small' : 'default'}>
          <div style={{ 
            textAlign: 'center', 
            marginBottom: window.innerWidth < 768 ? 20 : 32 
          }}>
            <Title
              level={window.innerWidth < 768 ? 3 : 2}
              style={{ marginBottom: window.innerWidth < 768 ? 4 : 8 }}
            >
              {isOrganizerFlow
                ? (window.innerWidth < 768 ? 'Sua conta' : 'Passo 1: sua conta')
                : (window.innerWidth < 768 ? 'Cadastro' : 'Cadastro de Jogador')}
            </Title>
            {isOrganizerFlow && (
              <Text type="secondary" style={{ display: 'block', marginBottom: window.innerWidth < 768 ? 12 : 20, fontSize: window.innerWidth < 768 ? 12 : 14 }}>
                Antes do time, criamos sua conta na comunidade. Depois vamos direto pro cadastro do time.
              </Text>
            )}
            <div style={{
              background: isOrganizerFlow ? '#eff6ff' : '#f6ffed',
              border: `1px solid ${isOrganizerFlow ? '#93c5fd' : '#b7eb8f'}`,
              borderRadius: '6px',
              padding: window.innerWidth < 768 ? '12px' : '16px',
              marginBottom: window.innerWidth < 768 ? 16 : 24,
              textAlign: 'left'
            }}>
              <Text strong style={{ color: isOrganizerFlow ? '#1d4ed8' : '#52c41a', fontSize: window.innerWidth < 768 ? 14 : 16 }}>
                {isOrganizerFlow
                  ? '⚡ Uma conta, dois papéis'
                  : '🎯 Como funciona o Futezap:'}
              </Text>
              <div style={{ marginTop: 8 }}>
                {isOrganizerFlow ? (
                  <>
                    <Text style={{ fontSize: window.innerWidth < 768 ? 12 : 14, display: 'block', marginBottom: 4 }}>
                      • <strong>Você é dono do time</strong> e também pode jogar. Mesma conta serve pros dois.
                    </Text>
                    <Text style={{ fontSize: window.innerWidth < 768 ? 12 : 14, display: 'block', marginBottom: 4 }}>
                      • <strong>Perfil público do time:</strong> jogadores da região te encontram no mapa
                    </Text>
                    <Text style={{ fontSize: window.innerWidth < 768 ? 12 : 14, display: 'block', marginBottom: 4 }}>
                      • <strong>Convocação, elenco, escalação:</strong> tudo num painel só
                    </Text>
                    <Text style={{ fontSize: window.innerWidth < 768 ? 12 : 14, display: 'block' }}>
                      • <strong>Cobrança e amistosos:</strong> menos correria no zap, mais bola
                    </Text>
                  </>
                ) : (
                  <>
                    <Text style={{ fontSize: window.innerWidth < 768 ? 12 : 14, display: 'block', marginBottom: 4 }}>
                      • <strong>Encontre times próximos:</strong> Veja times na sua região no mapa interativo
                    </Text>
                    <Text style={{ fontSize: window.innerWidth < 768 ? 12 : 14, display: 'block', marginBottom: 4 }}>
                      • <strong>Candidate-se para jogos:</strong> Aplique para vagas em jogos específicos
                    </Text>
                    <Text style={{ fontSize: window.innerWidth < 768 ? 12 : 14, display: 'block', marginBottom: 4 }}>
                      • <strong>Receba convites:</strong> Times podem te convidar diretamente
                    </Text>
                    <Text style={{ fontSize: window.innerWidth < 768 ? 12 : 14, display: 'block', marginBottom: 4 }}>
                      • <strong>Sistema de avaliações:</strong> Construa sua reputação jogando
                    </Text>
                    <Text style={{ fontSize: window.innerWidth < 768 ? 12 : 14, display: 'block' }}>
                      • <strong>Ganhe indicando:</strong> Receba 50% de comissão da primeira mensalidade do time indicado por você
                    </Text>
                  </>
                )}
              </div>
            </div>
            {!isOrganizerFlow && (
              <Text style={{ fontSize: window.innerWidth < 768 ? 13 : 14 }}>
                Junte-se à comunidade Futezap!
              </Text>
            )}
          </div>

          <Steps 
            current={currentStep} 
            items={steps} 
            size={window.innerWidth < 768 ? 'small' : 'default'}
            style={{ marginBottom: window.innerWidth < 768 ? 20 : 32 }} 
          />

          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={window.innerWidth < 768 ? [8, 8] : [24, 24]}>
              <Col xs={24} md={12}>
                <Form.Item name="name" label="Nome" rules={[{ required: true }]}>
                  <Input 
                    placeholder="Seu nome" 
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
                  <Input 
                    placeholder="seu@email.com" 
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={window.innerWidth < 768 ? [8, 8] : [24, 24]}>
              <Col xs={24} md={12}>
                <Form.Item name="password" label="Senha" rules={[{ required: true, min: 6 }]}>
                  <Input.Password 
                    placeholder="Sua senha" 
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="phone" label="Telefone" rules={[{ required: true }]}>
                  <Input 
                    placeholder="(11) 99999-9999" 
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={window.innerWidth < 768 ? [8, 8] : [24, 24]}>
              <Col xs={12} sm={12} md={6}>
                <Form.Item name="age" label="Idade" rules={[{ required: true, type: 'number', min: 16, max: 60 }]}>
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder="25" 
                    min={16} 
                    max={60}
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item name="skill_level" label="Nível" rules={[{ required: true }]}>
                  <Select 
                    placeholder="Selecione"
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                  >
                    {skillLevels.map(level => (
                      <Option key={level.value} value={level.value}>{level.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="preferred_position" label="Posição" rules={[{ required: true }]}>
                  <Select 
                    placeholder="Selecione" 
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                    onChange={(value) => {
                      if (value !== 'goleiro') {
                        form.setFieldsValue({ game_price: null });
                      }
                    }}
                  >
                    {positions.map(pos => (
                      <Option key={pos.value} value={pos.value}>{pos.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item 
                  noStyle 
                  shouldUpdate={(prevValues, currentValues) => 
                    prevValues.preferred_position !== currentValues.preferred_position
                  }
                >
                  {({ getFieldValue }) => {
                    const position = getFieldValue('preferred_position');
                    return position === 'goleiro' ? (
                      <Form.Item name="game_price" label={window.innerWidth < 768 ? 'Valor' : 'Valor por Jogo'}>
                        <InputNumber 
                          style={{ width: '100%' }} 
                          placeholder="50.00" 
                          min={0} 
                          precision={2}
                          addonBefore="R$"
                          size={window.innerWidth < 768 ? 'small' : 'default'}
                        />
                      </Form.Item>
                    ) : null;
                  }}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={window.innerWidth < 768 ? [8, 8] : [24, 24]}>
              <Col xs={24} md={12}>
                <Form.Item name="city" label="Cidade" rules={[{ required: true }]}>
                  <Input 
                    placeholder="São Paulo" 
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="address" label="Endereço" rules={[{ required: true }]}>
              <AddressInput
                placeholder="Rua, número, bairro, cidade"
                value={form.getFieldValue('address')}
                onChange={(value) => form.setFieldsValue({ address: value })}
                onCoordinatesChange={(coords) => {
                  form.setFieldsValue({
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                    city: coords.city || form.getFieldValue('city'),
                    state: coords.state || form.getFieldValue('state'),
                    neighborhood: coords.neighborhood || form.getFieldValue('neighborhood')
                  });
                }}
                size={window.innerWidth < 768 ? 'small' : 'default'}
              />
            </Form.Item>

            {window.innerWidth >= 768 && (
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="latitude" label="Latitude">
                    <InputNumber style={{ width: '100%' }} precision={8} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="longitude" label="Longitude">
                    <InputNumber style={{ width: '100%' }} precision={8} disabled />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Form.Item name="bio" label="Sobre Você">
              <Input.TextArea 
                rows={window.innerWidth < 768 ? 2 : 3} 
                placeholder="Conte sobre você..."
                size={window.innerWidth < 768 ? 'small' : 'default'}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size={window.innerWidth < 768 ? 'default' : 'large'}
                style={{ width: '100%' }}
              >
                {isOrganizerFlow ? 'Criar conta e continuar pro time' : 'Cadastrar'}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default PlayerRegister;